import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { createAvailability } from '../services/availabilityService';
import type { AvailabilityFormValues } from '../models';
import { AvailabilityStatuses, AvailabilityNeedTypes } from '../models';
import { availabilityQueryKeys } from './availabilityQueryKeys';

function ensureSlotId(slotIndex: number, slotId?: string): string {
  if (slotId && slotId.length > 0) {
    return slotId;
  }

  const randomSuffix = Math.random().toString(36).slice(2, 8);
  return `slot-${slotIndex}-${Date.now()}-${randomSuffix}`;
}

function sanitizeTextValue(value?: string | null): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeSlot(
  slot: AvailabilityFormValues['slots'][number],
  index: number
) {
  return {
    ...slot,
    slotId: ensureSlotId(index, slot.slotId),
    bucketValue: Number(slot.bucketValue ?? 0),
    workCity: sanitizeTextValue(slot.workCity) ?? null,
    workState: sanitizeTextValue(slot.workState) ?? null,
    capabilities: (slot.capabilities ?? []).map((capability) => ({
      typeId: capability.typeId,
      typeName: capability.typeName,
      technologyId: capability.technologyId ?? null,
      technologyName: capability.technologyName ?? null,
      seniorityId: capability.seniorityId ?? null,
      seniorityName: capability.seniorityName ?? null,
      required: Boolean(capability.required),
      isPrimary: Boolean(capability.isPrimary),
    })),
    candidates: slot.candidates ?? [],
    chosenCollaboratorId: slot.chosenCollaboratorId ?? null,
    lastSuggestionAt: slot.lastSuggestionAt
      ? Timestamp.fromDate(slot.lastSuggestionAt)
      : null,
    lastMatchFilters: slot.lastMatchFilters ?? null,
    lastMatchResultsCount:
      typeof slot.lastMatchResultsCount === 'number' && Number.isFinite(slot.lastMatchResultsCount)
        ? slot.lastMatchResultsCount
        : null,
    fulfillmentStrategy: slot.fulfillmentStrategy ?? 'known',
  };
}

export function buildAvailabilityDataFromForm(
  values: AvailabilityFormValues
) {
  return {
    clientId: values.clientId,
    clientName: values.clientName,
    clientLogo: values.clientLogo ?? null,
    initiative: values.initiative,
    status: values.status ?? AvailabilityStatuses.Simulation,
    needType: values.needType ?? AvailabilityNeedTypes.BodyShop,
    referenceDate: values.referenceDate ? Timestamp.fromDate(values.referenceDate) : null,
    notes: sanitizeTextValue(values.notes) ?? null,
    slots: values.slots.map((slot, index) => normalizeSlot(slot, index)),
    isInternalDemand: values.isInternalDemand ?? false,
    priorityLevel:
      typeof values.priorityLevel === 'number' ? values.priorityLevel : null,
  };
}

export function useCreateAvailabilityMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (values: AvailabilityFormValues) => {
      if (!user) {
        throw new Error('Usuário não autenticado.');
      }

      if (!values.clientId || !values.initiative) {
        throw new Error('Cliente e iniciativa são obrigatórios.');
      }

      const now = Timestamp.now();
      const availabilityData = buildAvailabilityDataFromForm(values);
      const resolvedUserName = user.displayName ?? user.email ?? 'Usuário Raro';

      return createAvailability({
        ...availabilityData,
        createdAt: now,
        updatedAt: now,
        createdById: user.uid,
        createdByName: resolvedUserName,
        createdByAvatar: user.photoURL ?? null,
        updatedById: user.uid,
        updatedByName: resolvedUserName,
        updatedByAvatar: user.photoURL ?? null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: availabilityQueryKeys.list() });
    },
  });
}

export type UseCreateAvailabilityMutationResult = ReturnType<
  typeof useCreateAvailabilityMutation
>;
